import { useEffect, useRef, useState } from 'react';
import { ContentWorkflow, WorkflowState } from '@amzn/awscat-pattern-library-service-model';

import { usePublisherReviewService } from '../../contexts/PublisherReviewServiceContext';
import {
  PublisherReviewResults,
  PublisherReviewScanState,
} from '../../services/PublisherReviewService';

export interface PublisherReviewState {
  readonly state: PublisherReviewScanState;
  readonly results: PublisherReviewResults | null;
}

export interface UsePublisherReviewStateOptions {
  readonly workflow?: ContentWorkflow;
  readonly contentId?: string;
}

/**
 * Subscribes to the PublisherReviewService and returns reactive state.
 *
 * When `workflow` and `contentId` are provided, automatically loads scan
 * results whenever the shown publisher-review content changes.
 */
export function usePublisherReviewState(
  options?: UsePublisherReviewStateOptions,
): PublisherReviewState {
  const service = usePublisherReviewService();
  const loadedContentIdRef = useRef<string | undefined>(undefined);

  const [snapshot, setSnapshot] = useState<PublisherReviewState>(() => ({
    state: service.state,
    results: service.results,
  }));

  // When the service emits, sync local snapshot.
  useEffect(() => {
    setSnapshot({
      state: service.state,
      results: service.results,
    });
    return service.on('changed', () => {
      setSnapshot({
        state: service.state,
        results: service.results,
      });
    });
  }, [service]);

  // When publisher-review content is shown (including after navigating from
  // another content item), load its scan results. Keyed purely on contentId:
  // the singleton service dedupes a repeat load for content it already tracks,
  // so gating on the snapshot here would wrongly skip the load when the service
  // still holds a prior content's results.
  const { workflow, contentId } = options ?? {};
  useEffect(() => {
    if (!workflow || !contentId) return;
    if (
      loadedContentIdRef.current !== contentId &&
      workflow.draftState === WorkflowState.PUBLISHER_REVIEW
    ) {
      loadedContentIdRef.current = contentId;
      service.loadResults(contentId);
    }
  }, [workflow, contentId, service]);

  return snapshot;
}
